import * as React from 'react';
import { useEffect, useReducer, useRef, useState } from 'react';
import {
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
} from 'reactstrap';
import InfiniteScroll from 'react-infinite-scroller';
import * as Models from '../models';
import {
  FloorplanListingService,
  SearchIn,
} from '../services/FloorplanListingService';
import classnames from 'classnames';

const PAGE_SIZE = 10;

class SearchType extends Models.LookupItem {
  ListingTitle: string;
}

class ProductType extends Models.LookupItem {
  categories: Models.LookupItem[];
}

type Props = {
  productTypes: ProductType[];
  provinces: Models.LookupItem[];
  dictionary: string[][];
  culture: string;
  //categories: LookupItem[];
};

type FloorplanListingPageState = {
  searchBy: SearchType;
  keywords: string;
  category: Models.LookupItem;
  province: Models.LookupItem;
  canLoadMore: boolean;
  isLoading: boolean;
  items: Models.FloorplanItem[];
  totalItems: number;
  lastFilter: SearchIn;
  lastSearchBy: SearchType;
  searchTrigger: number;
};

//const searchTypes: SearchType[] = [{ title: "All", value: '', ListingTitle: 'All Floorplans' },
//{ title: "Classrooms", value: 'classrooms', ListingTitle: 'Classrooms' },
//{ title: "Specialty", value: 'specialty', ListingTitle: 'Specialty' },
//{ title: "Used Buildings", value: 'usedbuildings', ListingTitle: 'Used Buildings' }
//];

const initialSearchType: SearchType = {
  title: 'All',
  value: '',
  ListingTitle: 'All Floorplans',
};

const initialState: FloorplanListingPageState = {
  searchBy: initialSearchType,
  keywords: '',
  category: undefined,
  province: undefined,
  canLoadMore: false,
  isLoading: false,
  items: [],
  totalItems: 0,
  lastFilter: undefined,
  lastSearchBy: initialSearchType,
  searchTrigger: 0,
};

const getUrlVars = (paramName) => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
};

const FloorplanListingPage = (props: Props) => {
  //const searchTypes: SearchType[] = [{ title: "All", value: '', ListingTitle: 'All Floorplans' },
  //{ title: "Classrooms", value: 'classrooms', ListingTitle: 'Classrooms' },
  //{ title: "Specialty", value: 'specialty', ListingTitle: 'Specialty' },
  //{ title: "Used Buildings", value: 'usedbuildings', ListingTitle: 'Used Buildings' }
  //];

  const SearchType = (title, value, listingTitle) => {
    return { title: title, value: value, ListingTitle: listingTitle };
  };

  const searchTypes: SearchType[] = [
    initialSearchType,
    ...props.productTypes.map((x) => SearchType(x.title, x.value, x.title)),
  ];

  const getInitialState = () => {
    const result = initialState;

    // read prov
    const provId = getUrlVars('prov') || '';
    if (provId.length > 0) {
      result.province = (props.provinces || []).find(
        (x) => x.value.toLowerCase() == provId.toLowerCase()
      );
    }

    // keywords
    result.keywords = getUrlVars('q') || '';

    // category
    const catTitle = getUrlVars('cat') || '';
    if (catTitle.length > 0) {
      let allCategories: Models.LookupItem[] = [];
      props.productTypes.forEach(
        (x) => (allCategories = allCategories.concat(x.categories))
      );

      result.category = allCategories.find(
        (x) => x.title.toLowerCase() == catTitle.toLowerCase()
      );
    }

    // result
    return result;
  };

  const [state, setState] = useReducer(
    (
      prevState: FloorplanListingPageState,
      newState: Partial<FloorplanListingPageState>
    ) => ({ ...prevState, ...newState }),
    getInitialState()
  );

  const search = async () => {
    const lastFilter: SearchIn = {
      keywords: state.keywords,
      category: state.category ? state.category.value : '',
      province: state.province ? state.province.value : '',
      searchBy: state.searchBy.value,
      culture: props.culture,
    };

    setState({
      isLoading: true,
      lastFilter: lastFilter,
      //items: [],
      lastSearchBy: state.searchBy,
    });

    //  update url
    {
      let paramsObj = {};
      if (state.keywords.length > 0) {
        paramsObj = {
          ...{ q: state.keywords },
          ...paramsObj,
        };
      }

      if (state.category) {
        paramsObj = {
          ...{ cat: state.category.title },
          ...paramsObj,
        };
      }

      if (state.province) {
        paramsObj = {
          ...{ prov: state.province.value },
          ...paramsObj,
        };
      }

      let urlParams = '?' + new URLSearchParams(paramsObj).toString();

      window.history.pushState({}, '', urlParams);
    }

    const result = await FloorplanListingService.search(
      lastFilter,
      PAGE_SIZE,
      0
    );

    if (!result.hasErrors) {
      setState({
        items: result.value.items,
        totalItems: result.value.totalItems,
        isLoading: false,
        canLoadMore: result.value.totalItems > result.value.items.length,
      });
    }
  };

  const loadMore = async () => {
    setState({
      isLoading: true,
    });

    const result = await FloorplanListingService.search(
      state.lastFilter,
      PAGE_SIZE,
      state.items.length
    );

    if (!result.hasErrors) {
      const newItemsSet = state.items.concat(result.value.items);

      setState({
        items: newItemsSet,
        totalItems: result.value.totalItems,
        isLoading: false,
        canLoadMore: result.value.totalItems > newItemsSet.length,
      });
    }
  };

  useEffect(() => {
    if (!state.category) {
      setState({ searchTrigger: state.searchTrigger + 1 });
      return;
    }
  }, [state.searchBy]);

  useEffect(() => {
    search();
  }, [state.searchTrigger, state.category, state.province]);

  useEffect(() => {
    search();

    //console.log('floor plans props', props);
  }, []);

  const currentProdType = props.productTypes.find(
    (x) => x.value == state.searchBy.value
  );

  return (
    <>
      <div className='container mb-3'>
        <div className='row'>
          <div className='col'>
            <div>
              <ul className='nav nav-pills'>
                {searchTypes.map((x) => (
                  <li className='nav-item' key={x.title}>
                    <a
                      className={classnames({
                        'nav-link': true,
                        active: state.searchBy.value == x.value,
                      })}
                      href='#'
                      onClick={(e) => {
                        e.preventDefault();
                        setState({
                          searchBy: x,
                          category: { ...(x as any), isSection: true },
                        });
                      }}
                    >
                      {x.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <form className='row align-items-end listing-filters'>
              <div className='col-lg-auto col-sm-4 pe-0 mb-3'>
                <div className='form-group'>
                  <label>Categories</label>
                  <UncontrolledDropdown>
                    <DropdownToggle
                      disabled={
                        state.searchBy.value == ''
                          ? false
                          : !currentProdType ||
                            !currentProdType.categories.length
                      }
                    >
                      {state.category
                        ? state.category.title
                        : state.searchBy.value == ''
                        ? 'All Categories'
                        : state.searchBy.title}
                    </DropdownToggle>
                    <DropdownMenu>
                      {state.category && (
                        <DropdownItem
                          onClick={() => {
                            setState({ category: undefined });
                          }}
                        >
                          {state.searchBy.value == ''
                            ? 'All Categories'
                            : state.searchBy.title}
                        </DropdownItem>
                      )}

                      {props.productTypes
                        .filter((x) =>
                          state.searchBy.value == ''
                            ? true
                            : x.value == state.searchBy.value
                        )
                        .map((x) => (
                          <React.Fragment key={x.title}>
                            {state.searchBy.value == '' && (
                              <DropdownItem
                                onClick={() => {
                                  setState({
                                    searchBy: {
                                      title: x.title,
                                      value: x.value,
                                      ListingTitle: x.title,
                                    },
                                    category: { ...x, isSection: true },
                                  });
                                }}
                                active={
                                  state.category &&
                                  state.category.value === x.value &&
                                  (state.category as any).isSection
                                }
                                style={{ fontWeight: 'bold' }}
                              >
                                {x.title}
                              </DropdownItem>
                            )}
                            {x.categories.map((y) => (
                              <DropdownItem
                                key={y.value}
                                onClick={() => {
                                  setState({
                                    searchBy: {
                                      title: x.title,
                                      value: x.value,
                                      ListingTitle: x.title,
                                    },
                                    category: y,
                                  });
                                }}
                                {...{
                                  active:
                                    state.category &&
                                    y.value == state.category.value,
                                }}
                              >
                                {y.title}
                              </DropdownItem>
                            ))}
                          </React.Fragment>
                        ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </div>
              {props.provinces && props.provinces.length > 0 && (
                <div className='col-lg-auto col-sm-4 pe-0 mb-3'>
                  <div className='form-group'>
                    <label>Province</label>
                    <UncontrolledDropdown>
                      <DropdownToggle>
                        {state.province
                          ? state.province.title
                          : 'All Provinces'}
                      </DropdownToggle>
                      <DropdownMenu>
                        {state.province && (
                          <DropdownItem
                            onClick={() => {
                              setState({ province: undefined });
                            }}
                          >
                            All Provinces
                          </DropdownItem>
                        )}

                        {props.provinces.map((x) => (
                          <DropdownItem
                            key={x.value}
                            onClick={() => {
                              setState({ province: x });
                            }}
                            {...{
                              active:
                                state.province &&
                                x.value == state.province.value,
                            }}
                          >
                            {x.title}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              )}
              <div className='col-lg-auto col-sm-4 pe-0 mb-3'>
                <div className='form-group'>
                  <label>Search</label>
                  <input
                    type='text'
                    onChange={(e) => {
                      setState({ keywords: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        search();
                      }
                    }}
                    placeholder='Keywords Search'
                    className='form-control'
                  />
                </div>
              </div>
              <div className='col-auto col-xs-12 mb-3 pe-0'>
                <div className='form-group mt-lg-3'>
                  <button
                    type='button'
                    className='btn btn-primary d-block w-100'
                    onClick={search}
                  >
                    Search
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className='bg-grey pb-1'>
        <div className='container'>
          <div className='row'>
            <div className='col'>
              <h3 className='my-3'>{state.lastSearchBy.ListingTitle}</h3>
            </div>
          </div>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={state.canLoadMore && !state.isLoading}
            useWindow={true}
            className='row row-cols-1 row-cols-sm-2 row-cols-md-3 cards-wrapper'
          >
            {state.items.map((item) => {
              return (
                <div className='col' key={item.nodeId}>
                  {' '}
                  <a
                    className='card card-scale-transition w-100 mb-4'
                    href={item.url}
                  >
                    {item.thumbnail && (
                      <div
                        className='image-container'
                        style={{
                          backgroundImage: "url('" + item.thumbnail + "')",
                        }}
                      ></div>
                    )}
                    {item.thumbnail && (
                      <img
                        className='card-img-top'
                        src={item.thumbnail}
                        alt={item.thumbnailTitle}
                      />
                    )}
                    <div className='card-body d-flex flex-column'>
                      {item.category && (
                        <div className='type-pill gold d-inline-flex align-items-center'>
                          {item.category.title}
                        </div>
                      )}
                      <h5
                        className='card-title'
                        dangerouslySetInnerHTML={{ __html: item.title }}
                      ></h5>
                    </div>
                  </a>
                </div>
              );
            })}
          </InfiniteScroll>
          <div
            className='text-center'
            style={{
              marginTop: '-12px',
              height: '45px',
            }}
          >
            {state.isLoading && <Spinner color='primary' />}
            {!state.items.length && (
              <div
                className='h3'
                dangerouslySetInnerHTML={{
                  __html: props.dictionary['custom.noresults'],
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FloorplanListingPage;
